import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LoadingState } from "../components/LoadingState";
import { ReportCard } from "../components/ReportCard";
import { UnavailableState } from "../components/UnavailableState";
import { useFtmDataService } from "../context/FtmDataServiceContext";
import type { Report, ReportCategory } from "../types";
import type { PageInfo } from "../publication/types";

const filters = ["all", "macro", "crypto", "technology", "global"] as const;

export function ReportsPage() {
  const service = useFtmDataService();
  const [category, setCategory] = useState<(typeof filters)[number]>("all");
  const [data, setData] = useState<Report[] | null>(null);
  const [page, setPage] = useState<PageInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setData(null);
    setPage(null);
    const request = { category: category === "all" ? undefined : category as ReportCategory, limit: 20 };
    const load = service.listReportsPage
      ? service.listReportsPage(request)
      : service.listReports(category === "all" ? {} : { category: category as ReportCategory }).then((items) => ({ items, page: { limit: items.length, nextCursor: null, hasMore: false } }));
    load.then((result) => {
      if (!active) return;
      setData(result.items);
      setPage(result.page);
      setLoading(false);
    }).catch((reason: unknown) => {
      if (!active) return;
      setError(reason instanceof Error ? reason.message : "The current report provider is unavailable.");
      setLoading(false);
    });
    return () => { active = false; };
  }, [category, service]);

  const loadMore = useCallback(async () => {
    if (!page?.nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const request = { category: category === "all" ? undefined : category as ReportCategory, cursor: page.nextCursor, limit: page.limit };
      const result = service.listReportsPage
        ? await service.listReportsPage(request)
        : { items: [] as Report[], page: { ...page, nextCursor: null, hasMore: false } };
      setData((current) => [...(current ?? []), ...result.items]);
      setPage(result.page);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "The next report page is unavailable.");
    } finally {
      setLoadingMore(false);
    }
  }, [category, loadingMore, page, service]);

  if (loading) return <LoadingState label="Loading current FTM Reports." />;
  if (error || !data) return <UnavailableState title="Reports could not load." message={error ?? "The current report provider is unavailable."} />;

  return <main id="main-content" className="site-shell page-space">
    <div className="page-heading-row"><div><p className="eyebrow">FTM REPORTS / CURRENT</p><h1 className="page-title page-title-compact">Reports</h1><p className="page-intro">Market-native context first. FTM estimate ranges and the complete evidence trail where access allows.</p></div><Link className="button-secondary" to="/pro">Unlock all reports <span aria-hidden="true">↗</span></Link></div>
    <div className="filter-row" role="group" aria-label="Filter reports">{filters.map((item) => <button type="button" className={`filter-button ${category === item ? "filter-button-active" : ""}`} key={item} onClick={() => setCategory(item)}>{item === "all" ? "All" : item[0].toUpperCase() + item.slice(1)}</button>)}</div>
    <div className="list-context-row"><span>{data.length} {data.length === 1 ? "report" : "reports"}</span><span>FTM ranges are independent estimates, not market prices.</span></div>
    {data.length ? <div className="report-grid report-grid-list">{data.map((report) => <ReportCard key={report.reportId} report={report} variant="list" isFeatured={report.featured && report.accessTier === "free"} />)}</div> : <div className="empty-state"><p className="eyebrow">NO MATCHES</p><p className="empty-state-title">No Reports match this filter.</p><p className="empty-state-copy">Try another category or return to all current reports.</p></div>}{page?.hasMore ? <button type="button" className="button-secondary" onClick={loadMore} disabled={loadingMore}>{loadingMore ? "Loading…" : "Load more"}</button> : null}
  </main>;
}
